import React from 'react';
import { motion } from 'framer-motion';
import Container from '../components/Container';
import SectionHeading from '../components/SectionHeading';
import ProjectShowcase from '../components/ProjectShowcase';
import Tag from '../components/Tag';
import { featuredProjects, otherProjects } from '../data/content';

const ProjectCard = ({ project, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.5, delay: index * 0.08, ease: 'easeOut' }}
    className="group rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-6 shadow-sm hover:shadow-md transition-all duration-300"
  >
    <div className="flex items-start justify-between gap-4 mb-3">
      <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
        {project.title}
      </h3>
      <span className="text-xs font-medium text-neutral-400 dark:text-neutral-500 shrink-0 mt-0.5">
        {project.date}
      </span>
    </div>

    <p className="text-sm leading-relaxed text-neutral-600 dark:text-neutral-400 mb-4">
      {project.description}
    </p>

    <div className="flex flex-wrap gap-1.5 mb-4">
      {project.tech.map((t) => (
        <Tag key={t} variant="proficient">
          {t}
        </Tag>
      ))}
    </div>

    <div className="flex items-center gap-4">
      {project.github && (
        <a
          href={project.github}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
          </svg>
          Source
        </a>
      )}
      {project.deployed && (
        <a
          href={project.deployed}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
          </svg>
          Live
        </a>
      )}
    </div>
  </motion.div>
);

const Projects = () => (
  <section id="projects" className="py-20 sm:py-28">
    <Container>
      {/* Featured projects */}
      <SectionHeading>Featured Projects</SectionHeading>
      <div className="mt-10 mb-20 space-y-8">
        {featuredProjects.map((project) => (
          <ProjectShowcase key={project.title} project={project} />
        ))}
      </div>

      {/* Other projects */}
      <SectionHeading>Other Projects</SectionHeading>
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {otherProjects.map((project, i) => (
          <ProjectCard key={project.title} project={project} index={i} />
        ))}
      </div>
    </Container>
  </section>
);

export default Projects;
